import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { appName } from '../../utils/brand';
import { LS } from '../../utils/storage';
import { ensureMessageAccount } from '../../utils/messagePolling';
import { Icons } from '../../components/Icons';
import { SectionTitle, track } from '../../components/kit';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { OnlineIndicator, SocialErrorCard } from './shared';
import { ReportUser } from '../../components/ReportUser';

export function SocialFriends({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  ensureMessageAccount(LS,Object.keys(localStorage),email);
  const displayName=s.profile?.firstName||email?.split("@")[0]||"You";
  const [friendsData,setFriendsData]=useState(null);
  const [addMethod,setAddMethod]=useState("code");
  const [addInput,setAddInput]=useState("");
  const [loading,setLoading]=useState(true);
  const [loadErr,setLoadErr]=useState(false);
  const [viewFriend,setViewFriend]=useState(null);
  // Tracks the latest opened friend so stale getFriendWorkouts responses don't overwrite the current view
  const viewFriendEmailRef=useRef(null);
  const [compareWith,setCompareWith]=useState(null);
  const [friendWorkouts,setFriendWorkouts]=useState(null);
  // Global discover people
  const [showDiscover,setShowDiscover]=useState(false);
  const [discoverQ,setDiscoverQ]=useState("");
  const [discoverResults,setDiscoverResults]=useState(null);
  const [discoverLoading,setDiscoverLoading]=useState(false);
  const [discoverPage,setDiscoverPage]=useState(1);
  const [partnersOnly,setPartnersOnly]=useState(false);
  // People you may know (server-ranked: mutual friends, same city/state, gym partners)
  const [suggestions,setSuggestions]=useState(null);
  const [requested,setRequested]=useState({}); // friendCode → true once a request was sent
  useEffect(()=>{if(email)SocialAPI.getSuggestions(email).then(r=>setSuggestions(r?.suggestions||[]));},[email,friendsData?.friends?.length]);
  const addSuggested=async(u)=>{
    Haptic.light();
    const r=await SocialAPI.sendRequest(email,u.friendCode);
    if(r?.success){setRequested(x=>({...x,[u.friendCode]:true}));SuccessToastCtrl.show(`Request sent to ${u.firstName||u.username||"them"}`);}
    else SuccessToastCtrl.error(r?.error||"Could not send request");
  };
  // Invite people who aren't on IRONLOG yet.
  // The friends response carries `me.friendCode`; s.profile only has it once the Profile
  // screen has been opened, so an invite built from the profile alone can go out with an
  // empty code. Prefer the server's copy and keep the share controls off until it is known.
  const myCode=friendsData?.me?.friendCode||s.profile?.friendCode||"";
  const canInvite=!!myCode;
  const inviteUrl=`https://ironlog.space/add?code=${myCode}${LS.get("ft-username")?`&u=${LS.get("ft-username")}`:""}`;
  const inviteText=`Train with me on ${appName()} — add me with code ${myCode}: ${inviteUrl}`;
  const canPickContacts=typeof navigator!=="undefined"&&"contacts" in navigator&&typeof navigator.contacts?.select==="function";
  const inviteFromContacts=async()=>{
    try{
      const picked=await navigator.contacts.select(["name","email","tel"],{multiple:true});
      if(!picked?.length)return;
      const emails=picked.flatMap(c=>c.email||[]).map(e=>String(e).toLowerCase()).filter(e=>/@/.test(e));
      const phones=picked.flatMap(c=>c.tel||[]).map(String);
      let added=0;const notOnApp=[];
      for(const em of emails){
        const r=await SocialAPI.sendRequest(email,em);
        if(r?.success)added++;else notOnApp.push(em);
      }
      if(added)loadFriends();
      if(notOnApp.length)window.open(`mailto:?bcc=${encodeURIComponent(notOnApp.join(","))}&subject=${encodeURIComponent(`Train with me on ${appName()}`)}&body=${encodeURIComponent(inviteText)}`,"_self");
      else if(!emails.length&&phones.length)window.open(`sms:${phones[0]}?&body=${encodeURIComponent(inviteText)}`,"_self");
      SuccessToastCtrl.show(added?`${added} friend request${added>1?"s":""} sent${notOnApp.length?` · ${notOnApp.length} invited`:""}`:notOnApp.length?"Invite drafted":"No email addresses found");
    }catch(e){/* user cancelled */}
  };
  const shareInvite=async()=>{
    if(!canInvite){SuccessToastCtrl.show("Your friend code hasn't loaded yet");return;}
    if(navigator.share){try{await navigator.share({title:`Add me on ${appName()}`,text:inviteText,url:inviteUrl});return;}catch(e){}}
    navigator.clipboard?.writeText(inviteText);SuccessToastCtrl.show("Invite link copied");
  };
  const block=(target)=>{ConfirmCtrl.show("Block this person?","They won't be able to message you, send requests, or see you in search. You can unblock from Profile → Privacy.",async()=>{
    const r=await SocialAPI.blockUser(email,target);
    if(!r||r.error||r.success===false||r.ok===false){SuccessToastCtrl.error(r?.error||"Could not block this person. Try again.");return;}
    setViewFriend(null);loadFriends();SuccessToastCtrl.show("Blocked");});};
  // Monotonic counter so an older in-flight response can't overwrite a newer one (fast-typing race)
  const discoverSeqRef=useRef(0);
  const discoverSearch=(q,page)=>{
    const seq=++discoverSeqRef.current;
    setDiscoverLoading(true);
    SocialAPI.discoverUsers(email,q||"",page||1,partnersOnly).then(r=>{
      if(seq!==discoverSeqRef.current)return;
      setDiscoverResults(r);setDiscoverLoading(false);
    });
  };
  // Re-run when the discover panel opens OR the typed query changes; the seq guard handles overlap
  useEffect(()=>{if(showDiscover)discoverSearch(discoverQ,1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[showDiscover,discoverQ,partnersOnly]);
  const streak=useStreak(s.workouts);
  const getBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{if(st.weight>b)b=st.weight;});}));return b;};
  const myBig3=getBest("bench")+getBest("squat")+getBest("deadlift");

  const friendsLoadRef=useRef(0);
  const loadFriends=useCallback(()=>{
    const seq=++friendsLoadRef.current;
    setLoadErr(false);
    if(!email){setFriendsData(null);setLoading(false);return;}
    setLoading(true);
    SocialAPI.getFriends(email).then(d2=>{
      if(seq!==friendsLoadRef.current)return;
      if(d2)setFriendsData(d2);else setLoadErr(true);
      setLoading(false);
    });
  },[email]);
  useEffect(()=>{
    setFriendsData(null);loadFriends();
    // Invalidate request generations (this ref is a counter, not a DOM node).
    const invalidate=()=>{friendsLoadRef.current++;};
    return invalidate;
  },[loadFriends]);

  const sendAdd=async()=>{
    if(!addInput.trim())return;
    let target=addInput.trim();
    // A code may be typed in any spelling and the server resolves all of them; just
    // don't send a stray @ or the surrounding whitespace. Emails keep their case-
    // insensitive handling server-side.
    if(!target.includes("@"))target=target.replace(/^@/,"");
    const r=await SocialAPI.sendRequest(email,target);
    if(r?.success){SuccessToastCtrl.show("Request sent!");setAddInput("");loadFriends();}
    else SuccessToastCtrl.error(r?.error||"Not found");
  };
  // SocialAPI returns null on a network error or any non-2xx, and the server can answer
  // {success:false,error:"…"} — never report a mutation as done without checking.
  const accept=async(from)=>{
    const r=await SocialAPI.acceptRequest(email,from);
    if(r?.success){SuccessToastCtrl.show("Friend added!");loadFriends();}
    else SuccessToastCtrl.error(r?.error||"Couldn't accept that request — check your connection and try again");
  };
  const remove=async(t)=>{ConfirmCtrl.show("Remove this friend?","You can re-add them later.",async()=>{
    const r=await SocialAPI.removeFriend(email,t);
    if(r?.success){loadFriends();SuccessToastCtrl.show("Removed");}
    else SuccessToastCtrl.error(r?.error||"Couldn't remove them — check your connection and try again");
  });};
  // Declining an incoming request is not unfriending, even though it is the same endpoint.
  const decline=async(from)=>{ConfirmCtrl.show("Decline this request?","They won't be told. They can send a new request later.",async()=>{
    const r=await SocialAPI.removeFriend(email,from);
    if(r?.success){loadFriends();SuccessToastCtrl.show("Request declined");}
    else SuccessToastCtrl.error(r?.error||"Couldn't decline that request — check your connection and try again");
  });};
  // Messaging lives in one place (Messages tab). Hand off the friend and switch tabs —
  // MessagesTab consumes ft-open-dm-from once its friend list has loaded.
  const openFriendChat=(f)=>{
    LS.set("ft-open-dm-from",f.email);
    d({type:"TAB",tab:"social_messages"});
  };
  const openFriendProfile=(f)=>{
    setViewFriend(f);setFriendWorkouts(null);
    // Track the latest target so a fast click-through (A then B) doesn't show A's workouts on B's profile
    viewFriendEmailRef.current=f.email;
    const target=f.email;
    SocialAPI.getFriendWorkouts(email,target).then(r=>{
      if(viewFriendEmailRef.current===target)setFriendWorkouts(r);
    });
  };

  // Deep-link: consume ft-pending-add (set by QR scan / shared link)
  // Pre-fills the add-friend input; auto-sends only if not already friends
  useEffect(()=>{
    const pendingCode=LS.get("ft-pending-add");
    if(!pendingCode||!email)return;
    LS.set("ft-pending-add",null); // clear immediately — don't fire twice
    setAddMethod("code");
    setAddInput(pendingCode);
    setTimeout(async()=>{
      // B4 fix: check if already friends before sending — sendRequest returns
      // {success:false, error:"Request already exists"} for dupes which is confusing
      const alreadyFriend=friendsData?.friends?.some(f=>
        f.friend_code===pendingCode||f.code===pendingCode
      );
      if(alreadyFriend){
        const f=friendsData.friends.find(fr=>fr.friend_code===pendingCode||fr.code===pendingCode);
        if(f)openFriendChat(f);
        SuccessToastCtrl.show("Already friends! 👋");
        setAddInput("");
        return;
      }
      const r=await SocialAPI.sendRequest(email,pendingCode);
      if(r?.success){
        SuccessToastCtrl.show("Friend request sent! 🎉");
        setAddInput("");
        loadFriends();
      } else if(r?.error==="Request already exists"){
        SuccessToastCtrl.show("Request already sent — check your pending requests");
      } else {
        // Keep input pre-filled so user can see the code
        SuccessToastCtrl.error(r?.error||"Code not found — double-check and try again");
      }
    },600);
  },[email]);
  const sendQuickMsg=async(fe,msg)=>{await SocialAPI.logEvent(email,"QuickMessage",{to:fe,msg},"friends");SuccessToastCtrl.show("Sent!");};

  if(compareWith)return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <button onClick={()=>setCompareWith(null)} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span>
      </button>
      <div style={{fontSize:16,fontWeight:800,color:V.text,textAlign:"center"}}>You vs {compareWith.name}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:8,alignItems:"center"}}>
        {[{l:"Streak",my:streak,their:compareWith.challenges?.find(c=>c.challenge_id==="streak")?.value||"?"},
          {l:"Big 3",my:myBig3||"—",their:compareWith.challenges?.find(c=>c.challenge_id==="big3")?.value||"?"},
          {l:"Workouts",my:(s.workouts||[]).length,their:"?"}].map((r,i)=>(
          <React.Fragment key={i}>
            <div style={{textAlign:"center",padding:10,borderRadius:10,background:parseFloat(r.my)>=parseFloat(r.their)?V.accent+"08":V.card}}>
              <div style={{fontSize:20,fontWeight:800,color:parseFloat(r.my)>=parseFloat(r.their)?V.accent:V.text,fontFamily:V.mono}}>{r.my}</div>
            </div>
            <div style={{fontSize:11,color:V.text3,fontWeight:600,textAlign:"center"}}>{r.l}</div>
            <div style={{textAlign:"center",padding:10,borderRadius:10,background:parseFloat(r.their)>parseFloat(r.my)?V.purple+"08":V.card}}>
              <div style={{fontSize:20,fontWeight:800,color:parseFloat(r.their)>parseFloat(r.my)?V.purple:V.text,fontFamily:V.mono}}>{r.their}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:4}}>
        <div style={{textAlign:"center",fontSize:11,color:V.accent,fontWeight:600}}>You</div>
        <div style={{textAlign:"center",fontSize:11,color:V.purple,fontWeight:600}}>{compareWith.name}</div>
      </div>
    </div>
  );

  if(viewFriend)return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <button onClick={()=>setViewFriend(null)} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span>
      </button>
      {/* Profile */}
      <>
        <Card style={{padding:0,overflow:"hidden",textAlign:"center"}}>
          {/* Banner */}
          <div style={{width:"100%",height:90,
            background:viewFriend.banner?"none":`linear-gradient(135deg,${V.purple}40,${V.accent}40)`,overflow:"hidden",flexShrink:0}}>
            {viewFriend.banner&&<img src={viewFriend.banner} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
          </div>
          {/* Avatar overlapping banner */}
          <div style={{padding:"0 16px 16px",marginTop:-30}}>
            <div style={{width:60,height:60,borderRadius:16,
              background:viewFriend.avatar?"none":`linear-gradient(135deg,${V.purple},${V.accent})`,
              display:"inline-flex",alignItems:"center",justifyContent:"center",
              overflow:"hidden",border:`3px solid ${V.bg}`,boxShadow:"0 4px 12px rgba(0,0,0,0.4)",marginBottom:8}}>
              {viewFriend.avatar
                ?<img src={viewFriend.avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                :<span style={{fontSize:22,color:V.bg,fontWeight:900}}>{(viewFriend.name||"?")[0].toUpperCase()}</span>}
            </div>
            <div style={{fontSize:16,fontWeight:800,color:V.text}}>{friendDisplayName(viewFriend)}</div>
            {/* Show their Iron Score rank if available */}
            {(()=>{
              const theirXP=parseInt(viewFriend.challenges?.find?.(c=>c.challenge_id==="ironscore")?.value)||0;
              if(theirXP>0){
                const theirRank=[...IRON_RANKS].reverse().find(r=>theirXP>=r.xpNeeded)||IRON_RANKS[0];
                return <div style={{marginBottom:4}}><RankBadge rank={theirRank} size="sm"/></div>;
              }
              return null;
            })()}
            {viewFriend.username&&<div style={{fontSize:11,color:V.text3}}>@{viewFriend.username}</div>}
            {viewFriend.bio&&<div style={{fontSize:11,color:V.text2,lineHeight:1.5,fontStyle:"italic",margin:"6px auto",maxWidth:260}}>{viewFriend.bio}</div>}
            {viewFriend.badges?.length>0&&(
              <div style={{display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center",margin:"10px 0"}}>
                {viewFriend.badges.map((b,i)=>{
                  const def=BADGE_DEFS.find(bd=>bd.name===b||bd.id===b);
                  return(
                    <div key={i} title={def?.desc||b} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <div style={{width:38,height:38,borderRadius:10,
                        background:`linear-gradient(135deg,${V.accent}15,${V.purple}15)`,
                        border:`1px solid ${V.accent}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                        {def?.icon||"🏅"}
                      </div>
                      <span style={{fontSize:11,color:V.accent,fontWeight:600,maxWidth:44,textAlign:"center",lineHeight:1.2}}>{def?.name||b}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {viewFriend.challenges?.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,margin:"10px 0"}}>
                {viewFriend.challenges.map((c,i)=>(
                  <div key={i} style={{padding:6,borderRadius:6,background:V.card}}>
                    <div style={{fontSize:14,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{c.value}</div>
                    <div style={{fontSize:11,color:V.text3,textTransform:"uppercase"}}>{c.challenge_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
        {/* Recent workouts */}
        {friendWorkouts&&!friendWorkouts.private&&(friendWorkouts.workouts||[]).length>0&&(
          <Card style={{padding:14}}>
            <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>🏋️ Recent Activity</div>
            {friendWorkouts.workouts.map((w,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",
                borderBottom:i<friendWorkouts.workouts.length-1?`1px solid ${V.divider}`:"none"}}>
                <div>
                  <div style={{fontSize:12,color:V.text}}>{fmtShort(w.date)}</div>
                  <div style={{fontSize:11,color:V.text3}}>{w.exerciseCount} exercise{w.exerciseCount!==1?"s":""}</div>
                </div>
                {w.duration&&<div style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{w.duration}min</div>}
              </div>
            ))}
          </Card>
        )}
        {friendWorkouts?.private&&(
          <Card style={{padding:14,textAlign:"center"}}><div style={{fontSize:11,color:V.text3}}>🔒 Workouts are private</div></Card>
        )}
        {/* #2: Streak comparison */}
        {viewFriend.challenges?.find(c=>c.challenge_id==="streak")&&(
          <Card style={{padding:14}}>
            <SectionTitle icon={Icons.flame} color={V.warn} size={12} style={{marginBottom:8}}>Streak battle</SectionTitle>
            {(()=>{
              const theirStreak=parseInt(viewFriend.challenges.find(c=>c.challenge_id==="streak")?.value)||0;
              const diff=streak-theirStreak;
              const winning=diff>0,tied=diff===0;
              return(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{textAlign:"center",flex:1}}>
                      <div style={{fontSize:28,fontWeight:800,color:winning||tied?V.accent:V.text,fontFamily:V.mono}}>{streak}</div>
                      <div style={{fontSize:11,color:V.text3}}>You</div>
                    </div>
                    <div style={{fontSize:16,color:V.text3,padding:"0 12px"}}>{winning?">":(tied?"=":"<")}</div>
                    <div style={{textAlign:"center",flex:1}}>
                      <div style={{fontSize:28,fontWeight:800,color:!winning&&!tied?V.purple:V.text,fontFamily:V.mono}}>{theirStreak}</div>
                      <div style={{fontSize:11,color:V.text3}}>{viewFriend.name?.split(" ")[0]}</div>
                    </div>
                  </div>
                  <div style={{textAlign:"center",fontSize:11,fontWeight:600,
                    color:winning?V.accent:(tied?V.warn:V.purple)}}>
                    {winning?`You're ahead by ${diff} day${diff>1?"s":""}! 💪`:tied?"Tied! Don't let them pass you 🤝":`They're ahead by ${Math.abs(diff)} day${Math.abs(diff)>1?"s":""}. Catch up! 🏃`}
                  </div>
                </div>
              );
            })()}
          </Card>
        )}
        <div style={{display:"flex",gap:6}}>
          <Btn full onClick={()=>openFriendChat(viewFriend)}>Message</Btn>
          <Btn v="secondary" full onClick={()=>{setViewFriend(null);setCompareWith(viewFriend);}}>Compare</Btn>
          <Btn v="secondary" full onClick={()=>remove(viewFriend.email)}>Remove</Btn>
        </div>
        <button type="button" onClick={()=>block(viewFriend.email)} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font,padding:"2px 0",textAlign:"center"}}>Block {friendDisplayName(viewFriend)}</button>
        <ReportUser key={viewFriend.email} target={viewFriend.email} name={friendDisplayName(viewFriend)}/>
        {/* Quick competitive actions */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          <button onClick={()=>{
            const rivals=LS.get("ft-rivals")||[];
            const already=rivals.find(r=>r.email===viewFriend.email);
            if(!already){
              LS.set("ft-rivals",[...rivals,{email:viewFriend.email,name:friendDisplayName(viewFriend),avatar:viewFriend.avatar,addedDate:today()}]);
              SuccessToastCtrl.show(`${friendDisplayName(viewFriend)} is now your rival! 🎯`);
              SocialAPI.logEvent(email,"RivalAdded",{rival:viewFriend.email},"friends");
            } else {
              SuccessToastCtrl.show("Already a rival");
            }
          }} style={{padding:"10px 8px",borderRadius:10,background:`${V.danger}14`,
            border:`1px solid ${V.danger}40`,cursor:"pointer",
            fontSize:11,fontWeight:700,color:V.danger,fontFamily:V.font}}>
            🎯 Add as Rival
          </button>
          <button onClick={()=>{d({type:"TAB",tab:"social_duels"});}} style={{padding:"10px 8px",borderRadius:10,
            background:`${V.accent}08`,border:`1px solid ${V.accent}25`,cursor:"pointer",
            fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>
            ⚔️ Start Duel
          </button>
        </div>
        {/* Accountability Partner */}
        <Card style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:V.text}}>🤝 Accountability Partner</div>
              <div style={{fontSize:11,color:V.text3,marginTop:2}}>Get nudged if either of you misses 2 days</div>
            </div>
            {(()=>{
              const partners=LS.get("ft-accountability")||[];
              const isPartner=partners.includes(viewFriend.email);
              return(
                <div onClick={()=>{
                  const current=LS.get("ft-accountability")||[];
                  if(isPartner){LS.set("ft-accountability",current.filter(e=>e!==viewFriend.email));SuccessToastCtrl.show("Partner removed");}
                  else{LS.set("ft-accountability",[...current,viewFriend.email]);SuccessToastCtrl.show(`${viewFriend.name} is now your accountability partner!`);
                    SocialAPI.logEvent(email,"AccountabilitySet",{partner:viewFriend.email,name:viewFriend.name},"friends");}
                }} style={{width:40,height:22,borderRadius:11,background:isPartner?V.accent:track(),padding:2,cursor:"pointer",transition:"all .2s"}}>
                  <div style={{width:18,height:18,borderRadius:9,background:"#fff",transform:isPartner?"translateX(18px)":"translateX(0)",transition:"transform .2s"}}/>
                </div>
              );
            })()}
          </div>
        </Card>
      </>
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Friends</div>
      {loadErr&&<SocialErrorCard onRetry={loadFriends}/>}
      <Card style={{padding:14}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>Add Friend</div>
        <div style={{display:"flex",gap:4,marginBottom:10}}>
          {[{id:"code",l:"Friend Code"},{id:"username",l:"Username"},{id:"email",l:"Email"}].map(m=>(
            <button key={m.id} onClick={()=>{setAddMethod(m.id);setAddInput("");}} style={{flex:1,padding:"6px",borderRadius:6,
              border:`1.5px solid ${addMethod===m.id?V.accent:V.cardBorder}`,background:addMethod===m.id?V.accent+"08":"transparent",
              cursor:"pointer",fontSize:11,fontWeight:600,color:addMethod===m.id?V.accent:V.text3,fontFamily:V.font}}>{m.l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <input value={addInput} onChange={e=>setAddInput(addMethod==="code"?e.target.value.toUpperCase():e.target.value)}
            placeholder={addMethod==="code"?"IRON-XXXXXX or XXXXXX":addMethod==="username"?"@username":"friend@email.com"}
            onKeyDown={e=>{if(e.key==="Enter")sendAdd();}}
            style={{flex:1,padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,
              color:V.text,fontSize:13,outline:"none",fontFamily:addMethod==="code"?V.mono:V.font,
              letterSpacing:addMethod==="code"?2:0}}/>
          <Btn onClick={sendAdd} disabled={!addInput.trim()}>Add</Btn>
        </div>
      </Card>

      {/* ── People you may know ── */}
      {suggestions?.length>0&&(
        <Card style={{padding:14}}>
          <SectionTitle icon={Icons.users} color={V.accent2} style={{marginBottom:6}}>People you may know</SectionTitle>
          <div style={{fontSize:11,color:V.text3,marginBottom:8}}>Based on mutual friends, your city and gym-partner preferences.</div>
          {suggestions.slice(0,6).map((u,i)=>(
            <div key={u.friendCode} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<Math.min(6,suggestions.length)-1?`1px solid ${V.divider}`:"none"}}>
              <div style={{width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,${V.accent2},${V.purple})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                {u.avatar?<img src={u.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
                :<span style={{fontSize:14,color:"#fff",fontWeight:900}}>{(u.firstName||u.username||"?")[0].toUpperCase()}</span>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:V.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.firstName||u.username||"Lifter"}{u.username?<span style={{fontWeight:400,color:V.text3}}> @{u.username}</span>:null}</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>
                  {u.reasons.map(r=><span key={r} style={{fontSize:11,padding:"2px 7px",borderRadius:6,background:track(),color:V.text2}}>{r}</span>)}
                </div>
              </div>
              <button type="button" disabled={!!requested[u.friendCode]} onClick={()=>addSuggested(u)}
                style={{padding:"7px 12px",borderRadius:8,border:"none",cursor:requested[u.friendCode]?"default":"pointer",fontFamily:V.font,fontSize:12,fontWeight:700,
                  background:requested[u.friendCode]?V.inputBg:V.accent,color:requested[u.friendCode]?V.text3:V.bg,flexShrink:0}}>
                {requested[u.friendCode]?"Sent":"Add"}
              </button>
            </div>
          ))}
        </Card>
      )}

      {/* ── Invite friends ── */}
      <Card style={{padding:14}}>
        <SectionTitle icon={Icons.upload} color={V.purple} style={{marginBottom:6}}>Invite friends</SectionTitle>
        <div style={{fontSize:11,color:V.text3,marginBottom:10}}>
          {canInvite?"Friends who join with your link are added automatically.":"Loading your friend code — the invite link needs it."}
        </div>
        <div style={{display:"grid",gridTemplateColumns:canPickContacts?"1fr 1fr":"1fr 1fr 1fr",gap:6}}>
          {canPickContacts&&<Btn full disabled={!canInvite} onClick={inviteFromContacts}>From contacts</Btn>}
          <Btn v={canPickContacts?"secondary":"primary"} full disabled={!canInvite} onClick={shareInvite}>Share link</Btn>
          {!canPickContacts&&<Btn v="secondary" full disabled={!canInvite} onClick={()=>window.open(`sms:?&body=${encodeURIComponent(inviteText)}`,"_self")}>Text</Btn>}
          {!canPickContacts&&<Btn v="secondary" full disabled={!canInvite} onClick={()=>window.open(`mailto:?subject=${encodeURIComponent(`Train with me on ${appName()}`)}&body=${encodeURIComponent(inviteText)}`,"_self")}>Email</Btn>}
        </div>
        {canPickContacts&&<div style={{display:"flex",gap:6,marginTop:6}}>
          <Btn v="secondary" full disabled={!canInvite} onClick={()=>window.open(`sms:?&body=${encodeURIComponent(inviteText)}`,"_self")}>Text</Btn>
          <Btn v="secondary" full disabled={!canInvite} onClick={()=>window.open(`mailto:?subject=${encodeURIComponent(`Train with me on ${appName()}`)}&body=${encodeURIComponent(inviteText)}`,"_self")}>Email</Btn>
        </div>}
      </Card>

      {/* ── Discover People (global) ── */}
      <Card style={{padding:14}}>
        <button onClick={()=>setShowDiscover(!showDiscover)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",cursor:"pointer",padding:0}}>
          <span style={{fontSize:12,fontWeight:700,color:V.accent}}>🌍 Discover People</span>
          <span style={{fontSize:11,color:V.text3}}>{showDiscover?"▲":"▼"}</span>
        </button>
        {showDiscover&&<div style={{marginTop:10}}>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input value={discoverQ} onChange={e=>setDiscoverQ(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"){setDiscoverPage(1);discoverSearch(discoverQ,1);}}}
              placeholder="Search by name or username..."
              style={{flex:1,padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}/>
            <Btn v="secondary" onClick={()=>{setDiscoverPage(1);discoverSearch(discoverQ,1);}}>Search</Btn>
          </div>
          <div style={{marginBottom:8}}><Chip label="Open to gym partners" active={partnersOnly} onClick={()=>setPartnersOnly(p=>!p)}/></div>
          {discoverLoading&&<div style={{textAlign:"center",padding:12,fontSize:11,color:V.text3}}>Loading...</div>}
          {!discoverLoading&&discoverResults&&(discoverResults.users||[]).length===0&&<div style={{textAlign:"center",padding:12,fontSize:11,color:V.text3}}>No users found</div>}
          {!discoverLoading&&(discoverResults?.users||[]).map((u,i)=>(
            <div key={u.friendCode||u.username||i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<discoverResults.users.length-1?`1px solid ${V.divider}`:"none"}}>
              <div style={{width:32,height:32,borderRadius:8,background:`linear-gradient(135deg,${V.purple},${V.accent})`,
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                {u.avatar?<img src={u.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
                :<span style={{fontSize:13,color:V.bg,fontWeight:900}}>{(u.firstName||u.nickname||"?")[0].toUpperCase()}</span>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:V.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.firstName||"User"}{u.nickname?` (${u.nickname})`:""}</div>
                {u.username&&<div style={{fontSize:11,color:V.text3}}>@{u.username}</div>}
              </div>
              {u.friendStatus==="accepted"?<span style={{fontSize:11,color:V.accent,fontWeight:600}}>Friends</span>
              :u.friendStatus==="pending"?<span style={{fontSize:11,color:V.warn,fontWeight:600}}>Pending</span>
              :<button onClick={async()=>{
                const target=u.friendCode||u.username||"";
                if(!target)return;
                const r=await SocialAPI.sendRequest(email,target);
                if(r?.success){SuccessToastCtrl.show("Request sent!");discoverSearch(discoverQ,discoverPage);}
                else SuccessToastCtrl.error(r?.error||"Could not add");
              }} style={{padding:"4px 10px",borderRadius:6,background:V.accent,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>Add</button>}
            </div>
          ))}
          {!discoverLoading&&discoverResults&&discoverResults.total>discoverResults.users?.length&&(
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:8}}>
              {discoverPage>1&&<Btn v="secondary" onClick={()=>{const p=discoverPage-1;setDiscoverPage(p);discoverSearch(discoverQ,p);}}>← Prev</Btn>}
              <Btn v="secondary" onClick={()=>{const p=discoverPage+1;setDiscoverPage(p);discoverSearch(discoverQ,p);}}>Next →</Btn>
            </div>
          )}
        </div>}
      </Card>

      {friendsData?.incoming?.length>0&&(
        <Card style={{padding:14,border:`1px solid ${V.accent}20`}}>
          <div style={{fontSize:12,fontWeight:700,color:V.accent,marginBottom:8}}>Requests ({friendsData.incoming.length})</div>
          {friendsData.incoming.map((r,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",
              borderBottom:i<friendsData.incoming.length-1?`1px solid ${V.divider}`:"none"}}>
              <span style={{fontSize:12,color:V.text}}>{r.name||"Someone"}</span>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>accept(r.from)} style={{padding:"5px 12px",borderRadius:6,background:V.accent,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>Accept</button>
                <button onClick={()=>decline(r.from)} aria-label={`Decline request from ${r.name||"this person"}`} title="Decline request"
                  style={{padding:"5px 10px",borderRadius:6,background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>Decline</button>
              </div>
            </div>
          ))}
        </Card>
      )}
      {friendsData?.friends?.length>0?(
        <div>
          {friendsData.friends.map((f,i)=>{
            const unread=parseInt(LS.get(`ft-unread-${f.email}`))||0;
            return(
            <Card key={i} style={{padding:12,marginBottom:6,cursor:"pointer"}} onClick={()=>openFriendProfile(f)}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{position:"relative",width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,${V.purple},${V.accent})`,
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:14,color:V.bg,fontWeight:900}}>{(f.name||"?")[0].toUpperCase()}</span>
                  {unread>0&&<div style={{position:"absolute",top:-4,right:-4,minWidth:16,height:16,borderRadius:8,
                    background:V.danger,border:`2px solid ${V.card}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontSize:11,fontWeight:800,color:"#fff"}}>{unread>9?"9+":unread}</span>
                  </div>}
                </div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}><span style={{fontSize:13,fontWeight:600,color:V.text}}>{f.name||"Unknown"}</span><OnlineIndicator lastActive={f.lastActive}/></div>
                  {f.username&&<div style={{fontSize:11,color:V.text3}}>@{f.username}</div>}
                </div>
                <div style={{display:"flex",gap:4}}>
                  <button onClick={e=>{e.stopPropagation();openFriendChat(f);}} style={{padding:"4px 8px",borderRadius:6,
                    background:unread>0?`${V.danger}15`:`${V.accent2}10`,border:`1px solid ${unread>0?V.danger+"30":V.accent2+"20"}`,
                    cursor:"pointer",fontSize:11,color:unread>0?V.danger:V.accent2,fontFamily:V.font,fontWeight:unread>0?700:400}}>
                    💬{unread>0?` ${unread}`:""}</button>
                  <button onClick={e=>{e.stopPropagation();setCompareWith(f);}} style={{padding:"4px 8px",borderRadius:6,
                    background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.accent,fontFamily:V.font}}>vs</button>
                  {Icons.chevRight({size:14,color:V.text3})}
                </div>
              </div>
            </Card>
            );}
          )}
        </div>
      ):(
        !loading&&!loadErr&&<Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>👥</div>
          <div style={{fontSize:13,fontWeight:700,color:V.text}}>No friends yet</div>
          <div style={{fontSize:11,color:V.text3,marginTop:4}}>Add friends using their code, username, or email.</div>
        </Card>
      )}
      {loading&&!loadErr&&<div><SkeletonCard lines={2}/><div style={{height:8}}/><SkeletonCard lines={3}/></div>}
    </div>
  );
}

